/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';
import RoundedInput from '../../components/RoundedInput';
import LiquidButton from '../../components/LiquidButton';
import { vocabularyApi } from '../../sdk';

interface AddVocabularyProps {
    onVocabularyAdded: () => void;
}

const Container = styled.div`
    margin: 0 auto 30px auto;
    max-width: 600px;
    padding: 20px;
    border-radius: 8px;
`;

const Title = styled.h3`
    margin-bottom: 25px;
    margin-top: 0;
    color: #6b46c1;
    font-size: 1.8rem;
    text-align: center;
    text-shadow: 0 2px 4px rgba(107, 70, 193, 0.2);
`;

const InputGroup = styled.div`
    margin-bottom: 10px;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #7c3aed;
    font-size: 1rem;
`;

const Message = styled.div<{ isSuccess?: boolean }>`
    margin-top: 15px;
    padding: 12px;
    border-radius: 8px;
    color: ${(props) => (props.isSuccess ? '#16a34a' : '#dc2626')};
    background: ${(props) =>
        props.isSuccess
            ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
            : 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'};
    border: 2px solid ${(props) => (props.isSuccess ? '#bbf7d0' : '#fecaca')};
    font-weight: 500;
`;

const ValidationError = styled.div`
    margin-top: 8px;
    color: #dc2626;
    font-size: 13px;
    font-weight: 500;
    padding: 8px 12px;
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
    border: 1px solid #fecaca;
    border-radius: 6px;
`;

const AddVocabulary: React.FC<AddVocabularyProps> = ({ onVocabularyAdded }) => {
    const auth = useAuth();
    const [newJapaneseWord, setNewJapaneseWord] = useState<string>('');
    const [newEnglishWord, setNewEnglishWord] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [submitMessage, setSubmitMessage] = useState<string>('');
    const [englishValidationError, setEnglishValidationError] = useState<string>('');
    const [japaneseValidationError, setJapaneseValidationError] = useState<string>('');

    // Validation function for English characters only
    const validateEnglishInput = (value: string): boolean => {
        // Allow letters, spaces, hyphens, and apostrophes (common in English words)
        const englishRegex = /^[a-zA-Z\s'-]*$/;
        return englishRegex.test(value);
    };

    // Validation function for Japanese characters only
    const validateJapaneseInput = (value: string): boolean => {
        // Allow Hiragana, Katakana, Kanji, and Japanese punctuation
        const hiraganaKatakana = '\u3040-\u309F\u30A0-\u30FF';
        const kanji = '\u4E00-\u9FAF';
        const japanesePunctuation = '\u3005\u3006\u3007\u30FC\u2010-\u2015\u2018-\u201F\u3000';
        const japaneseRegex = new RegExp(`^[${hiraganaKatakana}${kanji}${japanesePunctuation}]*$`);
        return japaneseRegex.test(value);
    };

    const handleJapaneseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        if (value === '' || validateJapaneseInput(value)) {
            setNewJapaneseWord(value);
            setJapaneseValidationError('');
        } else {
            setJapaneseValidationError(
                'Only Japanese characters (Hiragana, Katakana, Kanji) are allowed',
            );
        }
    };

    const handleEnglishInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        if (value === '' || validateEnglishInput(value)) {
            setNewEnglishWord(value);
            setEnglishValidationError('');
        } else {
            setEnglishValidationError(
                'Only English alphabet characters, spaces, hyphens, and apostrophes are allowed',
            );
        }
    };

    const submitNewWord = async () => {
        if (!newJapaneseWord.trim() || !newEnglishWord.trim()) {
            setSubmitMessage('Please enter both Japanese and English words');
            return;
        }

        setIsSubmitting(true);
        setSubmitMessage('');

        try {
            const response = await vocabularyApi.createVocabulary(
                {
                    Japanese_word: newJapaneseWord.trim(),
                    English_word: newEnglishWord.trim(),
                },
                auth.user?.access_token || '',
            );

            setSubmitMessage(response.message);
            setNewJapaneseWord('');
            setNewEnglishWord('');
            onVocabularyAdded();
        } catch (error) {
            if (error instanceof Error) {
                setSubmitMessage(`Failed to add word: ${error.message}`);
            } else {
                setSubmitMessage('Unknown error occurred while adding word');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container>
            <Title>Add New Vocabulary</Title>
            <InputGroup>
                <Label htmlFor="japanese-word-input">Japanese Word:</Label>
                <RoundedInput
                    id="japanese-word-input"
                    placeholder="Enter Japanese word"
                    value={newJapaneseWord}
                    onChange={handleJapaneseInputChange}
                />
                {japaneseValidationError && (
                    <ValidationError>{japaneseValidationError}</ValidationError>
                )}
            </InputGroup>
            <InputGroup>
                <Label htmlFor="english-word-input">English Translation:</Label>
                <RoundedInput
                    id="english-word-input"
                    placeholder="Enter English translation"
                    value={newEnglishWord}
                    onChange={handleEnglishInputChange}
                />
                {englishValidationError && (
                    <ValidationError>{englishValidationError}</ValidationError>
                )}
            </InputGroup>
            <LiquidButton onClick={submitNewWord} disabled={isSubmitting}>
                {isSubmitting ? 'Adding...' : 'Add Word'}
            </LiquidButton>
            {submitMessage && (
                <Message isSuccess={submitMessage.includes('successfully')}>
                    {submitMessage}
                </Message>
            )}
        </Container>
    );
};

export default AddVocabulary;
